import { useCallback, useMemo } from 'react';
import { useLocalStorageState } from '../../../hooks/useLocalStorageState';
import { employeeNameHaystack } from '../../../utils/employeeName';

export type EmployeeStatus = 'active' | 'inactive' | 'hired';

export type EmployeeStatusFilter = EmployeeStatus[] | 'all';

/** Statuses eligible for new project allocations (excludes leavers). */
export const STAFFING_ELIGIBLE_STATUSES: EmployeeStatus[] = ['active', 'hired'];

/**
 * Normalises status strings from HR integrations to the canonical three-value set.
 */
export function normaliseEmployeeStatus(raw: string | undefined): EmployeeStatus {
  const s = (raw ?? '').toLowerCase().trim();
  if (s === 'active' || s === 'employed' || s === 'current') return 'active';
  if (s === 'hired') return 'hired';
  return 'inactive';
}

/** True when an employee may be chosen for a new allocation. */
export function isStaffingEligibleEmployee(status?: string): boolean {
  return STAFFING_ELIGIBLE_STATUSES.includes(normaliseEmployeeStatus(status));
}

export interface PeopleFilters {
  search: string;
  status: EmployeeStatusFilter;
  /** Exact-match dropdown filters (null/undefined = no filter). */
  department?: string | null;
  role?: string | null;
  manager?: string | null;
}

export const DEFAULT_PEOPLE_FILTERS: PeopleFilters = {
  search: '',
  status: ['active', 'hired'],
  department: null,
  role: null,
  manager: null,
};

const STORAGE_KEY = 'teamlane:people:employees-filters:v3';

export interface UsePeopleFiltersReturn {
  filters: PeopleFilters;
  setSearch: (search: string) => void;
  setStatus: (status: EmployeeStatusFilter) => void;
  setDepartment: (department: string | null) => void;
  setRole: (role: string | null) => void;
  setManager: (manager: string | null) => void;
  resetFilters: () => void;
  /** True when any filter differs from the default. */
  isDirty: boolean;
}

/**
 * Per-user, per-browser filter state for the People → Employees tab.
 *
 * Scope: Employees tab only. The Upcoming Time Off and Orphaned tabs have
 * different semantics (orphaned rows are a fixed stale-sync cohort, not a
 * browsable roster — filtering it would hide the very rows the tab exists to
 * flag), so they must not share this state.
 */
export const usePeopleFilters = (): UsePeopleFiltersReturn => {
  const [stored, setStored] = useLocalStorageState<PeopleFilters>(
    STORAGE_KEY,
    DEFAULT_PEOPLE_FILTERS,
  );

  // Merge over defaults so a partially-shaped persisted value (or a future key
  // migration) always yields the full filter shape.
  const filters = useMemo<PeopleFilters>(
    () => ({ ...DEFAULT_PEOPLE_FILTERS, ...stored }),
    [stored],
  );

  const patch = useCallback(
    (partial: Partial<PeopleFilters>) =>
      setStored(prev => ({ ...DEFAULT_PEOPLE_FILTERS, ...prev, ...partial })),
    [setStored],
  );

  const setSearch = useCallback((search: string) => patch({ search }), [patch]);
  const setStatus = useCallback(
    (status: EmployeeStatusFilter) => patch({ status }),
    [patch],
  );
  const setDepartment = useCallback(
    (department: string | null) => patch({ department }),
    [patch],
  );
  const setRole = useCallback((role: string | null) => patch({ role }), [patch]);
  const setManager = useCallback(
    (manager: string | null) => patch({ manager }),
    [patch],
  );

  const resetFilters = useCallback(
    () => setStored(DEFAULT_PEOPLE_FILTERS),
    [setStored],
  );

  const isDirty = useMemo(
    () => {
      if (filters.search !== DEFAULT_PEOPLE_FILTERS.search) return true;
      if (filters.department !== null || filters.role !== null || filters.manager !== null) {
        return true;
      }
      if (filters.status === 'all' && DEFAULT_PEOPLE_FILTERS.status === 'all') return false;
      if (filters.status === 'all' || DEFAULT_PEOPLE_FILTERS.status === 'all') return true;
      const sorted = [...filters.status].sort();
      const defaultSorted = [...DEFAULT_PEOPLE_FILTERS.status].sort();
      return JSON.stringify(sorted) !== JSON.stringify(defaultSorted);
    },
    [filters.search, filters.status, filters.department, filters.role, filters.manager],
  );

  return {
    filters,
    setSearch,
    setStatus,
    setDepartment,
    setRole,
    setManager,
    resetFilters,
    isDirty,
  };
};

/**
 * Pure filter function, exposed for testability and reuse outside React.
 */
export function applyPeopleFilters<
  T extends {
    firstName?: string;
    lastName?: string;
    /** Preferred display name — full string, never split (see employeeName.ts). */
    displayName?: string | null;
    email?: string;
    department?: string;
    position?: string;
    status?: string;
    managerName?: string;
  },
>(employees: T[], filters: PeopleFilters): T[] {
  const q = filters.search.trim().toLowerCase();

  return employees.filter(emp => {
    if (filters.status !== 'all') {
      // Normalise raw HR statuses (e.g. "Terminated", "employed") to the
      // canonical set so a deactivated/terminated person surfaces under
      // "Inactive" (AC-9) — consistent with the directory and consultant-timeline
      // filters, which already normalise.
      if (!filters.status.includes(normaliseEmployeeStatus(emp.status))) return false;
    }

    // Exact-match dropdown filters (each null/undefined = no filter).
    if (filters.department && (emp.department ?? '') !== filters.department) return false;
    if (filters.role && (emp.position ?? '') !== filters.role) return false;
    if (filters.manager && (emp.managerName ?? '') !== filters.manager) return false;

    if (!q) return true;

    // Name + email via the shared haystack (matches preferred AND legal names).
    const haystack = [
      employeeNameHaystack(emp),
      emp.department,
      emp.position,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return haystack.includes(q);
  });
}
